import { ReloadIcon } from "@radix-ui/react-icons";
import {
  Coins,
  Receipt,
  type LucideIcon,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSWRConfig } from "swr";

import { BillingPlans } from "@/components/billing/BillingPlans";
import { AppShell, PageHeader } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useOrganizationScopes } from "@/lib/scopes";

import {
  AnalyticsEvents,
  captureAnalyticsEvent,
} from "@/analytics";
import {
  useGetBillingSession,
  useOrganization,
  useOrganizationInvoices,
  useSendSupportMessage,
  useSubscriptions,
} from "@/client/backend/organization";
import { useUser } from "@/client/backend/user";
import type { components } from "@/client/backend/zz_schema";
import { handleApiError } from "@/utils/api";
import { formatErrorMessage } from "@/utils/format";

type InvoiceBillingBreakdown = components["schemas"]["InvoiceBillingBreakdown"];
function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

function formatCurrency(amount: number): string {
  if (amount === 0) return "$0.00";
  if (amount >= 0.01) {
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function formatUnitAmount(amount: number): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  });
}

function formatDate(iso: string, timezone: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  });
}

function formatInvoicePeriod(invoice: InvoiceBillingBreakdown, timezone: string): string {
  const start = new Date(invoice.periodStart).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: timezone,
  });
  const end = new Date(invoice.periodEnd).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  });
  return `${start}–${end}`;
}

function SectionTitle({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
      <CardTitle>{children}</CardTitle>
    </div>
  );
}

export function Billing() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  const [organization] = useOrganization();
  const { can, loaded: scopesLoaded } = useOrganizationScopes(organization);
  // Nobody but an admin reaches billing; the data hooks below stay idle for
  // everyone else instead of collecting 403s.
  const canReadBilling = can("billing", "read");
  const orgId = organization === "loading" || !canReadBilling ? "loading" : organization.id;
  const userEmail = user !== "loading" ? user.emailAddress : "";
  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles",
    [],
  );

  const { data: subscriptionsResponse, mutate: refreshSubscriptions } = useSubscriptions(orgId);
  const { mutate } = useSWRConfig();
  const checkoutPrice = searchParams.get("checkout_success_price_id");
  const checkoutActivated = subscriptionsResponse?.data?.some(
    (s) => s.status === "Active" && s.stripePriceId === checkoutPrice,
  );
  const checkoutPending = !!checkoutPrice && !checkoutActivated;
  const [activationSlow, setActivationSlow] = useState(false);
  const { trigger: openBillingPortal } = useGetBillingSession(
    orgId,
    user !== "loading" ? user.emailAddress : undefined,
  );
  const { data: invoicesResponse, isLoading: realInvoicesLoading, error: invoicesError, mutate: refreshInvoices } = useOrganizationInvoices(
    orgId,
    { timezone },
  );
  const { trigger: sendSupportMessage } = useSendSupportMessage(orgId);

  useEffect(() => {
    if (orgId === "loading" || !checkoutPrice) return;
    if (checkoutActivated) {
      captureAnalyticsEvent(AnalyticsEvents.BILLING_CHECKOUT_SUCCESS_RETURNED, {});
      void mutate((key) => typeof key === "string" && (
        key.startsWith(`/organizations/${orgId}/`) || key.endsWith("/memberships")
      ));
      const next = new URLSearchParams(searchParams);
      next.delete("checkout_success_price_id");
      setSearchParams(next, { replace: true });
      return;
    }
    setActivationSlow(false);
    const interval = window.setInterval(() => {
      void refreshSubscriptions().catch(() => {});
    }, 2000);
    const timeout = window.setTimeout(() => {
      window.clearInterval(interval);
      setActivationSlow(true);
    }, 60000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [orgId, checkoutPrice, checkoutActivated, refreshSubscriptions, mutate, searchParams, setSearchParams]);

  const activeSubscriptions = useMemo(
    () => subscriptionsResponse?.data?.filter((s) => s.status === "Active") ?? [],
    [subscriptionsResponse],
  );
  const subscribed = activeSubscriptions.length > 0;
  const activeSubscription = activeSubscriptions.find((s) => s.planType !== "OnDemand") ?? activeSubscriptions[0];
  const isCanceling = !!activeSubscription?.cancelAt;
  const invoices = (invoicesResponse?.data?.invoices ?? []).map((invoice) => ({
    ...invoice, credits: Number(invoice.billedCredits ?? invoice.credits),
    tiers: invoice.tiers.map((tier) => ({ ...tier, credits: Number(tier.billedCredits ?? tier.credits) })),
  }));
  const invoicesLoading = realInvoicesLoading;
  const invoicesFailed = !!invoicesError || !!invoicesResponse?.error;

  // Spendable balance is the sum of both pools that usage draws down: the
  // organization's granted/trial credits (organization.creditMinutes) and, when
  // subscribed, the remaining plan credits across all active subscriptions. An
  // org can have more than one active subscription, and the cashier bills from
  // each, so we sum them rather than reading a single subscription.
  const planCredits = subscribed
    ? activeSubscriptions.reduce((sum, s) => sum + Number(s.remainingCredits ?? s.remainingMinutes ?? 0), 0)
    : 0;
  const grantedCredits = organization !== "loading" ? Number(organization.creditBalance ?? organization.creditMinutes) : 0;
  const credits = planCredits + grantedCredits;
  const creditsLabel = subscribed ? "Credits remaining" : "Free trial credits remaining";

  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const handleOpenBillingPortal = useCallback(async () => {
    if (orgId === "loading" || billingPortalLoading) return;
    captureAnalyticsEvent(AnalyticsEvents.BILLING_PORTAL_STARTED, {});
    setBillingPortalLoading(true);
    try {
      const result = await openBillingPortal();
      if (result?.data?.url) {
        const url = new URL(result.data.url);
        if (userEmail) url.searchParams.set("prefilled_email", userEmail);
        window.open(url.toString(), "_blank");
      } else {
        handleApiError(result, toast);
      }
    } catch (error) {
      toast({
        title: "Failed to open billing portal",
        description:
          error instanceof Error
            ? formatErrorMessage(error.message)
            : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setBillingPortalLoading(false);
    }
  }, [orgId, billingPortalLoading, openBillingPortal, userEmail, toast]);

  const [contactOpen, setContactOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  const openContactDialog = useCallback(() => {
    captureAnalyticsEvent(AnalyticsEvents.BILLING_CONTACT_STARTED, {
      reason: "volume_pricing",
    });
    setContactSent(false);
    setContactMessage("");
    setContactOpen(true);
  }, []);

  const handleSendContact = useCallback(async () => {
    if (orgId === "loading" || !contactMessage.trim() || contactSending) return;
    setContactSending(true);
    try {
      const result = await sendSupportMessage({
        message: `[Billing inquiry]\n\n${contactMessage.trim()}`,
      });
      if (handleApiError(result, toast)) {
        setContactSent(true);
      }
    } catch (error) {
      toast({
        title: "Failed to send message",
        description:
          error instanceof Error
            ? formatErrorMessage(error.message)
            : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setContactSending(false);
    }
  }, [orgId, contactMessage, contactSending, sendSupportMessage, toast]);

  const [expandedInvoiceIds, setExpandedInvoiceIds] = useState<Set<string>>(() => new Set());
  const toggleInvoiceDetails = useCallback((invoiceId: string) => {
    setExpandedInvoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  }, []);

  return (
    <AppShell title="Billing" width="reading">

      <PageHeader title="Billing" description="Manage your plan, payment method, and invoices." />

      {scopesLoaded && !canReadBilling && (
        <div className="grid w-full gap-6">
          <Card variant="section">
            <CardHeader>
              <CardTitle>Billing is for admins</CardTitle>
              <CardDescription>Your role in this organization can view it but not its billing. Ask an admin.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      )}
      <div className={`grid w-full gap-6 ${canReadBilling ? "" : "hidden"}`}>
        {checkoutPending && (
          <Card variant="section" role="status">
            <CardHeader>
              <CardTitle>{activationSlow ? "Your plan is still being activated" : "Activating your plan…"}</CardTitle>
              <CardDescription>We're waiting for payment confirmation from Stripe. Your credits will appear here once it arrives.</CardDescription>
            </CardHeader>
            {activationSlow && <CardContent><Button onClick={() => void refreshSubscriptions().catch(() => { })}>Check again</Button></CardContent>}
          </Card>
        )}
        {!checkoutPending && <Card variant="section">
          <CardHeader>
            <SectionTitle icon={Coins}>Credits</SectionTitle>
            <CardDescription>
              Available credit balance for running instances.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div
                className={`text-5xl font-semibold tracking-tight tabular-nums ${credits === 0 ? "text-muted-foreground/60" : ""
                  }`}
              >
                {formatNumber(credits)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{creditsLabel}</div>
            </div>
            {!subscribed && credits === 0 && (
              <p className="text-xs font-medium text-warning">
                You're out of free trial credits. Upgrade to keep building.
              </p>
            )}
            {!subscribed && credits > 0 && (
              <p className="text-xs text-muted-foreground">
                Free trial credits are granted at sign-up and don't expire.
              </p>
            )}
          </CardContent>
        </Card>}

        {!checkoutPending && <BillingPlans onContact={openContactDialog} onManage={handleOpenBillingPortal} />}

        {(invoicesLoading || invoicesFailed || invoices.length > 0) && (
          <Card variant="section">
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="grid gap-1">
                <SectionTitle icon={Receipt}>Invoices</SectionTitle>
                <CardDescription>
                  Current overage and recent Stripe invoices. Draft amounts can change as usage is processed.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={handleOpenBillingPortal}
                disabled={billingPortalLoading}
              >
                {billingPortalLoading && (
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                {billingPortalLoading ? "Opening" : "Manage in Stripe"}
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Usage credits</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Avg/usage credit</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoicesFailed && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div role="alert" className="flex flex-wrap items-center gap-2 py-3 text-sm">
                          <span>Could not update invoices. Any amounts shown may be out of date.</span>
                          <Button variant="outline" size="sm" onClick={() => void refreshInvoices()}>Retry</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {invoicesLoading ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                          <Spinner className="h-4 w-4" />
                          Fetching from Stripe
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : invoices.length === 0 && !invoicesFailed ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center text-sm text-muted-foreground"
                      >
                        No invoices yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((invoice) => {
                      const invoiceKey = invoice.previewSubscriptionId
                        ? `preview:${invoice.previewSubscriptionId}:${invoice.periodStart}`
                        : invoice.invoiceId;
                      const isExpanded = invoice.tiers.length > 0 && expandedInvoiceIds.has(invoiceKey);
                      const averageCostPerCredit =
                        invoice.credits > 0 && invoice.usageAmount != null
                          ? invoice.usageAmount / invoice.credits : null;
                      return (
                        <React.Fragment key={invoiceKey}>
                          <TableRow>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <button
                                  type="button"
                                  className="w-fit text-left font-medium hover:underline"
                                  disabled={invoice.tiers.length === 0}
                                  onClick={() => toggleInvoiceDetails(invoiceKey)}
                                >
                                  {formatInvoicePeriod(invoice, timezone)}
                                </button>
                                <div className="text-xs text-muted-foreground">
                                  {invoice.invoiceNumber || invoice.description}
                                </div>
                                {invoice.status === "draft" && (
                                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" aria-hidden="true" />
                                    Draft
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {invoice.credits > 0 || invoice.status === "draft" ? formatNumber(invoice.credits) : "—"}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatCurrency(invoice.amount)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {averageCostPerCredit == null ? "—" : formatUnitAmount(averageCostPerCredit)}
                            </TableCell>
                            <TableCell className="text-right">
                              {invoice.hostedInvoiceUrl ? (
                                <a
                                  href={invoice.hostedInvoiceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline"
                                >
                                  View
                                </a>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={5} className="bg-muted/40">
                                <div className="rounded-md border bg-background">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Description</TableHead>
                                        <TableHead className="text-right">Qty</TableHead>
                                        <TableHead className="text-right">Unit price</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      <TableRow>
                                        <TableCell>
                                          <div className="font-medium">
                                            Metered usage
                                          </div>
                                          <div className="text-xs text-muted-foreground">
                                            {formatInvoicePeriod(invoice, timezone)}
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                          {invoice.credits > 0 ? formatNumber(invoice.credits) : "—"}
                                        </TableCell>
                                        <TableCell className="text-right" />
                                        <TableCell className="text-right tabular-nums">
                                          {formatCurrency(invoice.usageAmount ?? invoice.amount)}
                                        </TableCell>
                                      </TableRow>
                                      {invoice.tiers.map((tier) => (
                                        <TableRow key={tier.lineItemId}>
                                          <TableCell className="pl-8 text-muted-foreground">
                                            {tier.label}
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums text-muted-foreground">
                                            {formatNumber(tier.credits)}
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums text-muted-foreground">
                                            {formatUnitAmount(tier.unitAmount)}
                                          </TableCell>
                                          <TableCell className="text-right tabular-nums text-muted-foreground">
                                            {formatCurrency(tier.amount)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              {invoices.some((invoice) => invoice.status === "draft") && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Current overage shows metered credits used and charges so far, after discounts and before taxes or prepaid credit grants.
                  Subscription fees are excluded. Recent usage may take time to appear. Draft invoices are not final.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {subscribed && (
          <div className="flex justify-center pt-2 text-xs">
            <button
              type="button"
              onClick={handleOpenBillingPortal}
              disabled={billingPortalLoading}
              className="text-muted-foreground transition-colors hover:text-destructive hover:underline disabled:opacity-50"
            >
              {billingPortalLoading
                ? "Opening Stripe..."
                : isCanceling
                  ? `Subscription cancels on ${formatDate(activeSubscription.cancelAt!, timezone)} — manage`
                  : "Cancel subscription"}
            </button>
          </div>
        )}
      </div>


      <Dialog
        open={contactOpen}
        onOpenChange={(open) => {
          setContactOpen(open);
          if (!open) {
            setContactSent(false);
            setContactMessage("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Talk to our team</DialogTitle>
            <DialogDescription>
              Tell us about your use case and expected volume. We'll get back to you at{" "}
              <span className="font-medium text-foreground">{userEmail || "your email"}</span>.
            </DialogDescription>
          </DialogHeader>
          {contactSent ? (
            <div className="rounded-md border bg-muted/40 p-4 text-sm">
              Thanks! We received your message and will reach out shortly
              {userEmail ? ` at ${userEmail}` : ""}.
            </div>
          ) : (
            <div className="grid gap-3 py-2">
              <Label htmlFor="contact-message">Your message</Label>
              <Textarea
                id="contact-message"
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                placeholder="Tell us about your team, platforms (iOS/Android/XCode), expected concurrency and minutes per month, and any compliance needs."
                rows={6}
              />
            </div>
          )}
          <DialogFooter>
            {contactSent ? (
              <Button onClick={() => setContactOpen(false)}>Done</Button>
            ) : (
              <Button
                onClick={handleSendContact}
                disabled={orgId === "loading" || !contactMessage.trim() || contactSending}
              >
                {contactSending && <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />}
                {contactSending ? "Sending" : "Send message"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
